// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ChargeStatus } from '@/constants/charge-status';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { getProjectBillingAggregate } from '@/project/api/get-project-billing-aggregate';
import { ProjectFilter } from '@/project/api/get-projects';
import {
  CurrencyBillingSummary,
  ProjectBillingAggregateResponse,
} from '@/project/api/types';
import { FIVE_MINUTES_MS, THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { useQuery } from '@tanstack/react-query';
import { Decimal } from 'decimal.js';
import { useMemo } from 'react';

export interface CurrencyBillingDatum {
  isoCode: IsoCurrencyCode;
  totalAmount: Decimal;
  totalEvents: number;
  amountsByStatus: Record<ChargeStatus, Decimal>;
}

interface ProjectBillingAggregateData {
  byCurrency: CurrencyBillingDatum[];
}

function transformCurrencySummary(
  summary: CurrencyBillingSummary
): CurrencyBillingDatum {
  const amountsByStatus: Record<ChargeStatus, Decimal> = {
    PLANNED: summary.planned,
    BILLABLE: summary.billable,
    INVOICED: summary.invoiced,
    CANCELLED: summary.cancelled,
  };

  const totalEvents = Object.values(amountsByStatus).filter((amount) =>
    amount.greaterThan(0)
  ).length;

  return {
    isoCode: summary.isoCode,
    totalAmount: summary.total,
    totalEvents,
    amountsByStatus,
  };
}

function transformBillingAggregateData(
  aggregateData: ProjectBillingAggregateResponse['data']
): ProjectBillingAggregateData {
  return {
    byCurrency: aggregateData.byCurrency.map(transformCurrencySummary),
  };
}

export function useProjectBillingAggregate(
  filter?: Omit<ProjectFilter, 'pageSize' | 'pageNumber' | 'sort'>
) {
  const queryResult = useQuery({
    queryKey: ['project-billing-aggregate', filter],
    queryFn: () => getProjectBillingAggregate(filter),
    staleTime: THIRTY_SECONDS_MS,
    gcTime: FIVE_MINUTES_MS,
  });

  const billingData = useMemo<ProjectBillingAggregateData>(() => {
    if (!queryResult.data?.data) {
      return {
        byCurrency: [],
      };
    }

    return transformBillingAggregateData(queryResult.data.data);
  }, [queryResult.data]);

  return {
    billingData,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
  } as const;
}
